import React, { useState, useEffect } from 'react';
import './BettingRecords.css';
import { useNavigate } from "react-router-dom";
import InnerHeader from '../../components/InnerHeader';

const BettingRecords = ({ onViewDetails }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('settled');
  
  // API states
  const [providers, setProviders] = useState([]);
  const [gameTypes, setGameTypes] = useState([]);
  const [bettingRecords, setBettingRecords] = useState([]);
  const [exchangeRecords, setExchangeRecords] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedDate, setSelectedDate] = useState("Last 7 days");
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedExchangeRecord, setSelectedExchangeRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Lazy loading states
  const [displayLimit, setDisplayLimit] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Save scroll position before navigating
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('bettingRecordsScrollPosition', window.scrollY.toString());
      sessionStorage.setItem('bettingRecordsDisplayLimit', displayLimit.toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save on component unmount (when navigating away)
      sessionStorage.setItem('bettingRecordsScrollPosition', window.scrollY.toString());
      sessionStorage.setItem('bettingRecordsDisplayLimit', displayLimit.toString());
    };
  }, [displayLimit]);

  // Restore scroll position and display limit
  useEffect(() => {
    if (!loading && bettingRecords.length > 0) {
      const savedScrollPosition = sessionStorage.getItem('bettingRecordsScrollPosition');
      const savedDisplayLimit = sessionStorage.getItem('bettingRecordsDisplayLimit');
      
      if (savedDisplayLimit) {
        setDisplayLimit(parseInt(savedDisplayLimit));
      }
      
      if (savedScrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition));
        }, 100);
      }
    }
  }, [loading, bettingRecords]);

  // Manual refresh function
  const handleRefresh = () => {
    // Clear cache
    sessionStorage.removeItem('bettingRecords');
    sessionStorage.removeItem('providers');
    sessionStorage.removeItem('gameTypes');
    sessionStorage.removeItem('bettingRecordsTimestamp');
    sessionStorage.removeItem('bettingRecordsScrollPosition');
    sessionStorage.removeItem('bettingRecordsDisplayLimit');
    
    // Reload page to fetch fresh data
    window.location.reload();
  };

  // API Call - Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      // Check if data already exists in sessionStorage
      const cachedRecords = sessionStorage.getItem('bettingRecords');
      const cachedProviders = sessionStorage.getItem('providers');
      const cachedGameTypes = sessionStorage.getItem('gameTypes');
      const cacheTimestamp = sessionStorage.getItem('bettingRecordsTimestamp');
      
      // If cache exists and is less than 5 minutes old, use cached data
      const isCacheValid = cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < 5 * 60 * 1000;
      
      if (isCacheValid && cachedRecords && cachedProviders && cachedGameTypes) {
        console.log('Using cached data');
        setBettingRecords(JSON.parse(cachedRecords));
        setProviders(JSON.parse(cachedProviders));
        setGameTypes(JSON.parse(cachedGameTypes));
        setLoading(false);
        return;
      }

      // Otherwise fetch fresh data
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      try {
        const [providersResponse, typesResponse, recordsResponse, exchangeResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/get-provider`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/get-type`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/betting-record`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/exchange-hub`, { headers }),
        ]);

        if (providersResponse.ok && typesResponse.ok) {
          const providersData = await providersResponse.json();
          const typesData = await typesResponse.json();
          setProviders(providersData.data);
          setGameTypes(typesData.types);
          
          // Cache providers and game types
          sessionStorage.setItem('providers', JSON.stringify(providersData.data));
          sessionStorage.setItem('gameTypes', JSON.stringify(typesData.types));
        }

        if (recordsResponse.ok) {
          const recordsData = await recordsResponse.json();
          console.log('Betting Records Data:', recordsData);
          console.log('Total Records:', recordsData.length);
          setBettingRecords(recordsData);
          
          // Cache betting records and timestamp
          sessionStorage.setItem('bettingRecords', JSON.stringify(recordsData));
          sessionStorage.setItem('bettingRecordsTimestamp', Date.now().toString());
        } else {
          const recordsError = await recordsResponse.json();
          console.warn(recordsError.message);
          setBettingRecords([]);
        }

        if (exchangeResponse.ok) {
          const exchangeData = await exchangeResponse.json();
          setExchangeRecords(exchangeData.data);
        } else {
          const exchangeError = await exchangeResponse.json();
          console.warn(exchangeError.message);
          setExchangeRecords([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Date filtering logic
  const isDateInRange = (date, range) => {
    const today = new Date();
    const txnDate = new Date(date);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    return range === "Today" ? txnDate.toDateString() === today.toDateString() :
           range === "Yesterday" ? txnDate.toDateString() === new Date(today.setDate(today.getDate() - 1)).toDateString() :
           range === "Last 7 days" ? txnDate >= lastWeek && txnDate <= today : true;
  };

  // Filter records based on settle/unsettle
  const filteredRecords = bettingRecords.filter(record => {
    const matchesPlatform = selectedPlatform === "All" || record.platform === selectedPlatform;
    const matchesType = selectedType === "All" || record.game_type === selectedType;
    const matchesDate = isDateInRange(record.created_at, selectedDate);
    
    // Check settle/unsettle based on action field
    const isSettled = record.action === "betNSettle";
    const matchesTab = activeTab === 'settled' ? isSettled : !isSettled;
    
    return matchesPlatform && matchesType && matchesDate && matchesTab;
  });

  console.log('Total Records from API:', bettingRecords.length);
  console.log('Filtered Records Count:', filteredRecords.length);
  console.log('Active Tab:', activeTab);

  // Reset display limit when filters change
  useEffect(() => {
    setDisplayLimit(20);
  }, [activeTab, selectedPlatform, selectedType, selectedDate]);

  // Convert each record to display format (NO GROUPING - show all records)
  const displayRecords = filteredRecords.map(record => {
    const betAmount = parseFloat(record.bet_amount) || 0;
    const winAmount = parseFloat(record.winAmount) || 0;
    const profitLoss = winAmount - betAmount;

    return {
      id: record.id,
      date: new Date(record.created_at).toDateString(),
      sport: record.platform,
      badge: record.game_type,
      turnover: betAmount.toFixed(2),
      icon: getIconSVG(record.game_type),
      gameName: record.game_name || record.game_type,
      result: profitLoss.toFixed(2),
      timestamp: new Date(record.created_at).toLocaleString(),
      details: {
        sport: record.platform,
        totalTurnover: betAmount.toFixed(2),
        transactions: [{
          id: record.id,
          name: record.game_name || record.game_type,
          turnover: betAmount.toFixed(2),
          result: profitLoss.toFixed(2),
          timestamp: new Date(record.created_at).toLocaleString(),
          platform_tx_id: record.platform_tx_id,
          round_id: record.round_id
        }]
      }
    };
  });

  // Apply display limit for lazy loading (show only first 'displayLimit' records)
  const limitedRecords = displayRecords.slice(0, displayLimit);

  // Group data by date for display
  const groupedByDate = limitedRecords.reduce((acc, bet) => {
    if (!acc[bet.date]) {
      acc[bet.date] = [];
    }
    acc[bet.date].push(bet);
    return acc;
  }, {});

  console.log('Display Records Count:', displayRecords.length);
  console.log('Showing Records:', limitedRecords.length);
  console.log('Has More Records:', displayRecords.length > displayLimit);

  // Get icon SVG based on game type
  function getIconSVG(gameType) {
    const icons = {
      'Cricket': '🏏',
      'Football': '⚽',
      'Tennis': '🎾',
      'Basketball': '🏀',
      'Hockey': '🏒',
      'Baseball': '⚾',
      'Rugby': '🏉',
      'Table': '🎲',
      'Exchange': '💱',
      'Slot': '🎰',
      'slot': '🎰',
      'Casino': '🎰',
      'casino': '🎰',
      'Poker': '🃏',
      'poker': '🃏',
    };
    const emoji = icons[gameType] || '🎮';
    const colors = {
      'Cricket': '#FF6B35',
      'Football': '#4CAF50',
      'Tennis': '#FFC107',
      'Basketball': '#FF9800',
      'Hockey': '#2196F3',
      'Baseball': '#E91E63',
      'Rugby': '#9C27B0',
      'Table': '#8BC34A',
      'Exchange': '#00BCD4',
      'Slot': '#FF5722',
      'slot': '#FF5722',
      'Casino': '#673AB7',
      'casino': '#673AB7',
      'Poker': '#00BCD4',
      'poker': '#00BCD4',
    };
    const color = colors[gameType] || '#666';
    
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' rx='20' fill='%23333'/%3E%3Ctext x='20' y='25' font-family='Arial' font-size='20' fill='${color.replace('#', '%23')}' text-anchor='middle'%3E${emoji}%3C/text%3E%3C/svg%3E`;
  }

  // Handle card click
  const handleCardClick = (bet) => {
    navigate('/betting-details', { state: { betDetails: bet } });
    if (onViewDetails) {
      onViewDetails(bet);
    }
  };

  // Load more records function
  const loadMoreRecords = () => {
    if (isLoadingMore || displayLimit >= displayRecords.length) return;
    
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayLimit(prev => prev + 20);
      setIsLoadingMore(false);
    }, 500);
  };

  // Scroll event listener for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // Check if user is near bottom of page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreRecords();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, displayLimit, displayRecords.length]);

  return (
    <div className="bets-container">
      {/* Header */}
      <InnerHeader title='Betting records'/>
      
      {/* Tabs */}
      <div className="bets-tabs">
        <button 
          className={`bets-tab ${activeTab === 'settled' ? 'bets-tab-active' : ''}`}
          onClick={() => setActiveTab('settled')}
        >
          Settled
        </button>
        <button 
          className={`bets-tab ${activeTab === 'unsettled' ? 'bets-tab-active' : ''}`}
          onClick={() => setActiveTab('unsettled')}
        >
          Unsettled
        </button>
      </div>

      {/* Filter Section */}
      <div className="bets-filter-section">
        <div className="bets-filter-dropdown">
          <span className="bets-filter-text">{selectedDate}</span>
        </div>
        <div className="bets-filter-actions">
          {/* <button className="bets-refresh-icon" onClick={handleRefresh} title="Refresh data">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M21 2v6h-6M3 22v-6h6M21 8a10 10 0 01-17.95 6.95M3 16a10 10 0 0117.95-6.95" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button> */}
          <button className="bets-filter-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M7 12h10M11 18h2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="bets-loader">
          <div className="bets-spinner"></div>
          <p>Loading betting records...</p>
        </div>
      ) : (
        <>
          {/* Betting Records List */}
          {Object.keys(groupedByDate).length > 0 ? (
            <>
              {Object.keys(groupedByDate).map((date) => (
                <div key={date}>
                  {/* Date */}
                  <div className="bets-date">{date}</div>

                  {/* Bet Cards for this date */}
                  {groupedByDate[date].map((bet) => (
                    <div 
                      key={bet.id} 
                      className="bets-card"
                      onClick={() => handleCardClick(bet)}
                    >
                      <div className="bets-card-left">
                        <div className="bets-icon-wrapper">
                          <img 
                            src={bet.icon}
                            alt={bet.sport} 
                            className="bets-sport-icon"
                          />
                        </div>
                        <div className="bets-card-info">
                          <span className="bets-sport-name">{bet.sport}</span>
                          <span className="bets-badge">{bet.badge}</span>
                        </div>
                      </div>
                      <div className="bets-card-right">
                        <span className="bets-turnover-label">Turnover</span>
                        <span className="bets-turnover-value">{bet.turnover}</span>
                        <svg className="bets-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18l6-6-6-6" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Load More Indicator */}
              {displayLimit < displayRecords.length && (
                <div className="bets-load-more">
                  {isLoadingMore ? (
                    <>
                      <div className="bets-spinner-small"></div>
                      <p>Loading more...</p>
                    </>
                  ) : (
                    <button onClick={loadMoreRecords} className="bets-load-more-btn">
                      Load More ({displayRecords.length - displayLimit} remaining)
                    </button>
                  )}
                </div>
              )}

              {/* End of Records */}
              {displayLimit >= displayRecords.length && displayRecords.length > 0 && (
                <div className="bets-end-message">
                  {/* <p>That's all! No more records to show.</p> */}
                </div>
              )}
            </>
          ) : (
            <div className="bets-no-data">
              <p style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
                No betting records found
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BettingRecords;